import React from 'react';

/**
 * Renders a <picture> that serves next-gen formats with graceful fallback.
 *
 * The browser picks the first <source> it supports (AVIF, then WebP), and
 * falls back to the plain <img> (PNG/JPEG) on anything older.
 *
 * Because the Parcel bundler hashes each format independently, callers must
 * import every variant explicitly and pass the resolved URLs in, e.g.:
 *
 *   import heroAvif from 'url:../../assets-optimized/.../hero.avif';
 *   import heroWebp from 'url:../../assets-optimized/.../hero.webp';
 *   import heroPng  from 'url:../../assets-optimized/.../hero.png';
 *   <OptimizedImage avif={heroAvif} webp={heroWebp} fallback={heroPng} alt="Hero" />
 *
 * Optionally accepts art-directed mobile variants. When provided, they are
 * emitted as media-scoped <source>s ahead of the desktop ones, so small
 * viewports load a differently-cropped image:
 *
 *   <OptimizedImage
 *     avif={...} webp={...} fallback={...}
 *     mobileAvif={...} mobileWebp={...} mobileFallback={...}
 *   />
 */
const OptimizedImage = ({
  avif,
  webp,
  fallback,
  mobileAvif,
  mobileWebp,
  mobileFallback,
  mobileMedia = '(max-width: 768px)',
  alt = '',
  className = '',
  ...imgProps
}) => {
  return (
    <picture>
      {mobileAvif && <source media={mobileMedia} srcSet={mobileAvif} type='image/avif' />}
      {mobileWebp && <source media={mobileMedia} srcSet={mobileWebp} type='image/webp' />}
      {mobileFallback && <source media={mobileMedia} srcSet={mobileFallback} />}
      {avif && <source srcSet={avif} type='image/avif' />}
      {webp && <source srcSet={webp} type='image/webp' />}
      <img src={fallback} alt={alt} className={className} {...imgProps} />
    </picture>
  );
};

export default OptimizedImage;
